import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiTrash2, FiSend, FiXCircle, FiPrinter, FiMail, FiShare2, FiEye, FiCheckCircle } from 'react-icons/fi';

const TABS = ['Customers', 'Products', 'Quotes', 'Invoices', 'Payments', 'Credit Notes'];
const emptyLine = { productServiceId: '', description: '', quantity: '1', unitPrice: '0.00', vatRateId: '' };

const printStyles = `
  @media print {
    @page { size: A4; margin: 1.2cm; }
    body * { visibility: hidden; }
    .invoice-print-area, .invoice-print-area * { visibility: visible; }
    .invoice-print-area {
      position: absolute; left: 0; top: 0; width: 100%; padding: 0;
      font-size: 11px;
    }
    .no-print { display: none !important; }
    .invoice-print-area .table-container {
      overflow: visible !important;
      border: 1px solid #d1d5db;
    }
    .invoice-print-area table.data-table {
      display: table !important; width: 100% !important;
      table-layout: fixed !important; border-collapse: collapse !important;
    }
    .invoice-print-area table.data-table thead { display: table-header-group !important; }
    .invoice-print-area table.data-table tbody { display: table-row-group !important; }
    .invoice-print-area table.data-table tr { display: table-row !important; }
    .invoice-print-area table.data-table th,
    .invoice-print-area table.data-table td {
      display: table-cell !important; padding: 0.4rem 0.6rem; font-size: 11px;
    }
  }
`;

const Invoicing = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Customers');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [organisation, setOrganisation] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [invoiceWorkflowId, setInvoiceWorkflowId] = useState(null);
  const [invoiceApprovalRequests, setInvoiceApprovalRequests] = useState([]);
  const [quoteWorkflowId, setQuoteWorkflowId] = useState(null);
  const [quoteApprovalRequests, setQuoteApprovalRequests] = useState([]);

  const [customerForm, setCustomerForm] = useState({ name: '', customerType: 'business', email: '', phone: '', paymentTermsDays: 30, vatNumber: '', currencyCode: '' });
  const [productForm, setProductForm] = useState({ sku: '', name: '', description: '', itemType: 'service', defaultUnitPrice: '', defaultVatRateCode: '' });
  const [invoiceForm, setInvoiceForm] = useState({ customerId: '', invoiceDate: new Date().toISOString().slice(0, 10), dueDate: '', notes: '', lines: [{ ...emptyLine }] });
  const [paymentForm, setPaymentForm] = useState({ customerId: '', amount: '', paymentMethod: 'bank_transfer', paidAt: new Date().toISOString().slice(0, 10), invoiceId: '' });
  const [creditNoteForm, setCreditNoteForm] = useState({ customerId: '', invoiceId: '', creditNoteDate: new Date().toISOString().slice(0, 10), lines: [{ description: '', quantity: '1', unitPrice: '0.00', vatRateId: '' }] });
  const [quoteForm, setQuoteForm] = useState({ customerId: '', quoteDate: new Date().toISOString().slice(0, 10), expiryDate: '', lines: [{ ...emptyLine }] });

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceLines, setInvoiceLines] = useState(null);
  const [invoicePayments, setInvoicePayments] = useState(null);
  const [invoiceCreditNotes, setInvoiceCreditNotes] = useState(null);

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteLines, setQuoteLines] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/invoicing/customers'),
      apiClient.get('/invoicing/products'),
      apiClient.get('/invoicing/invoices'),
      apiClient.get('/invoicing/payments'),
      apiClient.get('/invoicing/credit-notes'),
      apiClient.get('/vat/transactions/rates'),
      apiClient.get('/organisations/current'),
      apiClient.get('/lookups/currencies'),
      apiClient.get('/workflow/workflows', { params: { appliesToType: 'invoice' } }).catch(() => ({ data: { data: [] } })),
      apiClient.get('/workflow/approvals').catch(() => ({ data: { data: [] } })),
      apiClient.get('/invoicing/quotes'),
      apiClient.get('/workflow/workflows', { params: { appliesToType: 'quote' } }).catch(() => ({ data: { data: [] } })),
    ]).then(([custRes, prodRes, invRes, payRes, cnRes, vatRes, orgRes, currRes, wfRes, reqRes, quoteRes, quoteWfRes]) => {
      setCustomers(custRes.data.data);
      setProducts(prodRes.data.data);
      setInvoices(invRes.data.data);
      setPayments(payRes.data.data);
      setCreditNotes(cnRes.data.data);
      setVatRates(vatRes.data.data);
      setOrganisation(orgRes.data.data);
      setCurrencies(currRes.data.data);
      const activeInvoiceWorkflow = wfRes.data.data.find((w) => w.isActive);
      setInvoiceWorkflowId(activeInvoiceWorkflow ? activeInvoiceWorkflow.id : null);
      setInvoiceApprovalRequests(reqRes.data.data.filter((r) => r.entityType === 'invoice'));
      setQuotes(quoteRes.data.data);
      const activeQuoteWorkflow = quoteWfRes.data.data.find((w) => w.isActive);
      setQuoteWorkflowId(activeQuoteWorkflow ? activeQuoteWorkflow.id : null);
      setQuoteApprovalRequests(reqRes.data.data.filter((r) => r.entityType === 'quote'));
    }).catch((err) => showError(getErrorMessage(err, 'Could not load invoicing data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const findCustomer = (id) => customers.find((c) => c.id === id);
  const customerName = (id) => findCustomer(id)?.name || id;
  const companyName = organisation?.tradingName || organisation?.legalName || 'Your Company';

  const symbolForCurrencyId = (currencyId) => currencies.find((c) => c.id === currencyId)?.symbol || '£';
  const symbolForCustomer = (customerId) => symbolForCurrencyId(findCustomer(customerId)?.currencyId);
  const approvalForInvoice = (invoiceId) => invoiceApprovalRequests.find((r) => r.entityId === invoiceId);
  const approvalForQuote = (quoteId) => quoteApprovalRequests.find((r) => r.entityId === quoteId);

  // ---- Customers ----
  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...customerForm };
      if (!payload.currencyCode) delete payload.currencyCode;
      await apiClient.post('/invoicing/customers', payload);
      success('✅ Customer added');
      setShowForm(false);
      setCustomerForm({ name: '', customerType: 'business', email: '', phone: '', paymentTermsDays: 30, vatNumber: '', currencyCode: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add customer')); }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await apiClient.delete(`/invoicing/customers/${customerId}`);
      success('✅ Customer deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete customer')); }
  };

  // ---- Products ----
  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/invoicing/products', productForm);
      success('✅ Product added');
      setShowForm(false);
      setProductForm({ sku: '', name: '', description: '', itemType: 'service', defaultUnitPrice: '', defaultVatRateCode: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add product')); }
  };

  const handleToggleActive = async (productId, currentlyActive) => {
    try {
      await apiClient.patch(`/invoicing/products/${productId}`, { isActive: !currentlyActive });
      success(currentlyActive ? '✅ Item marked inactive' : '✅ Item reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update item')); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await apiClient.delete(`/invoicing/products/${productId}`);
      success('✅ Item deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete item')); }
  };

  // ---- Quotes ----
  const updateQuoteLine = (idx, field, value) => {
    setQuoteForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      if (field === 'productServiceId' && value) {
        const p = products.find((x) => x.id === value);
        if (p) {
          lines[idx].description = p.name;
          lines[idx].unitPrice = p.defaultUnitPrice || '0.00';
        }
      }
      return { ...prev, lines };
    });
  };
  const addQuoteLine = () => setQuoteForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeQuoteLine = (idx) => setQuoteForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
  const quoteLineTotal = (line) => (Number(line.quantity || 0) * Number(line.unitPrice || 0)).toFixed(2);
  const quoteFormTotal = quoteForm.lines.reduce((sum, l) => sum + Number(quoteLineTotal(l)), 0).toFixed(2);
  const quoteFormSymbol = symbolForCustomer(quoteForm.customerId);

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...quoteForm };
      if (!payload.expiryDate) delete payload.expiryDate;
      payload.lines = quoteForm.lines.map((l) => ({
        ...(l.productServiceId ? { productServiceId: l.productServiceId } : {}),
        description: l.description,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        ...(l.vatRateId ? { vatRateId: l.vatRateId } : {}),
      }));
      await apiClient.post('/invoicing/quotes', payload);
      success('✅ Quote created');
      setShowForm(false);
      setQuoteForm({ customerId: '', quoteDate: new Date().toISOString().slice(0, 10), expiryDate: '', lines: [{ ...emptyLine }] });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create quote')); }
  };

  const viewQuote = async (quote) => {
    setSelectedQuote(quote);
    setQuoteLines(null);
    try {
      const { data } = await apiClient.get(`/invoicing/quotes/${quote.id}/lines`);
      setQuoteLines(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load quote lines')); }
  };

  const handleConvertQuote = async (quoteId) => {
    try {
      await apiClient.post(`/invoicing/quotes/${quoteId}/convert-to-invoice`);
      success('✅ Quote converted to invoice — check the Invoices tab');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not convert quote')); }
  };

  const handleDeleteDraftQuote = async (quoteId) => {
    if (!window.confirm('Delete this draft quote?')) return;
    try {
      await apiClient.delete(`/invoicing/quotes/${quoteId}`);
      success('✅ Quote deleted');
      if (selectedQuote?.id === quoteId) setSelectedQuote(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete quote')); }
  };

  const handleSubmitQuoteForApproval = async (quote) => {
    if (!quoteWorkflowId) return;
    try {
      await apiClient.post('/workflow/approvals', {
        workflowId: quoteWorkflowId,
        entityType: 'quote',
        entityId: quote.id,
        amount: quote.total,
      });
      success('✅ Submitted for approval');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not submit for approval')); }
  };

  // ---- Invoices ----
  const updateInvoiceLine = (idx, field, value) => {
    setInvoiceForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      if (field === 'productServiceId' && value) {
        const p = products.find((x) => x.id === value);
        if (p) {
          lines[idx].description = p.name;
          lines[idx].unitPrice = p.defaultUnitPrice || '0.00';
        }
      }
      return { ...prev, lines };
    });
  };
  const addInvoiceLine = () => setInvoiceForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeInvoiceLine = (idx) => setInvoiceForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const invoiceLineTotal = (line) => (Number(line.quantity || 0) * Number(line.unitPrice || 0)).toFixed(2);
  const invoiceFormTotal = invoiceForm.lines.reduce((sum, l) => sum + Number(invoiceLineTotal(l)), 0).toFixed(2);
  const invoiceFormSymbol = symbolForCustomer(invoiceForm.customerId);

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...invoiceForm,
        lines: invoiceForm.lines.map((l) => ({
          ...(l.productServiceId ? { productServiceId: l.productServiceId } : {}),
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          ...(l.vatRateId ? { vatRateId: l.vatRateId } : {}),
        })),
      };
      await apiClient.post('/invoicing/invoices', payload);
      success('✅ Invoice created as draft');
      setShowForm(false);
      setInvoiceForm({ customerId: '', invoiceDate: new Date().toISOString().slice(0, 10), dueDate: '', notes: '', lines: [{ ...emptyLine }] });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create invoice')); }
  };

  const viewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceLines(null);
    setInvoicePayments(null);
    setInvoiceCreditNotes(null);
    try {
      const [linesRes, paymentsRes, creditNotesRes] = await Promise.all([
        apiClient.get(`/invoicing/invoices/${invoice.id}/lines`),
        apiClient.get('/invoicing/payments', { params: { invoiceId: invoice.id } }),
        apiClient.get('/invoicing/credit-notes', { params: { invoiceId: invoice.id } }),
      ]);
      setInvoiceLines(linesRes.data.data);
      setInvoicePayments(paymentsRes.data.data);
      setInvoiceCreditNotes(creditNotesRes.data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load invoice detail')); }
  };

  const handleIssueInvoice = async (invoiceId) => {
    try {
      await apiClient.post(`/invoicing/invoices/${invoiceId}/issue`);
      success('✅ Invoice issued');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not issue invoice')); }
  };

  const handleVoidInvoice = async (invoiceId) => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    try {
      await apiClient.post(`/invoicing/invoices/${invoiceId}/void`);
      success('✅ Invoice voided');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not void invoice')); }
  };

  const handleDeleteDraftInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this draft invoice? The invoice number stays skipped either way.')) return;
    try {
      await apiClient.delete(`/invoicing/invoices/${invoiceId}`);
      success('✅ Draft invoice deleted');
      if (selectedInvoice?.id === invoiceId) setSelectedInvoice(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete invoice')); }
  };

  const handleSubmitInvoiceForApproval = async (invoice) => {
    if (!invoiceWorkflowId) return;
    try {
      await apiClient.post('/workflow/approvals', {
        workflowId: invoiceWorkflowId,
        entityType: 'invoice',
        entityId: invoice.id,
        amount: invoice.total,
      });
      success('✅ Submitted for approval');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not submit for approval')); }
  };

  const handlePrint = () => window.print();

  const handleShareWhatsApp = () => {
    if (!selectedInvoice) return;
    const cust = findCustomer(selectedInvoice.customerId);
    const sym = symbolForCurrencyId(selectedInvoice.currencyId);
    const balance = (Number(selectedInvoice.total) - Number(selectedInvoice.amountPaid || 0)).toFixed(2);
    const lines = [
      `Invoice ${selectedInvoice.invoiceNumber || selectedInvoice.publicId} — ${companyName}`,
      `To: ${cust?.name || ''}`,
      `Date: ${new Date(selectedInvoice.invoiceDate).toLocaleDateString()}`,
      selectedInvoice.dueDate ? `Due: ${new Date(selectedInvoice.dueDate).toLocaleDateString()}` : null,
      `Total: ${sym}${Number(selectedInvoice.total).toFixed(2)}`,
      `Balance Due: ${sym}${balance}`,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleShareEmail = () => {
    if (!selectedInvoice) return;
    const cust = findCustomer(selectedInvoice.customerId);
    const sym = symbolForCurrencyId(selectedInvoice.currencyId);
    const balance = (Number(selectedInvoice.total) - Number(selectedInvoice.amountPaid || 0)).toFixed(2);
    const subject = encodeURIComponent(`Invoice ${selectedInvoice.invoiceNumber || selectedInvoice.publicId} from ${companyName}`);
    const body = encodeURIComponent(
      `Hi ${cust?.name || ''},\n\nPlease find your invoice details below.\n\n` +
      `From: ${companyName}\n` +
      `Invoice: ${selectedInvoice.invoiceNumber || selectedInvoice.publicId}\n` +
      `Date: ${new Date(selectedInvoice.invoiceDate).toLocaleDateString()}\n` +
      (selectedInvoice.dueDate ? `Due: ${new Date(selectedInvoice.dueDate).toLocaleDateString()}\n` : '') +
      `Total: ${sym}${Number(selectedInvoice.total).toFixed(2)}\n` +
      `Balance Due: ${sym}${balance}\n\nThank you.`
    );
    window.location.href = `mailto:${cust?.email || ''}?subject=${subject}&body=${body}`;
  };

  // ---- Payments ----
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/invoicing/payments', {
        customerId: paymentForm.customerId,
        amount: paymentForm.amount,
        paymentMethod: paymentForm.paymentMethod,
        paidAt: paymentForm.paidAt,
        allocations: [{ invoiceId: paymentForm.invoiceId, allocatedAmount: paymentForm.amount }],
      });
      success('✅ Payment recorded');
      setShowForm(false);
      setPaymentForm({ customerId: '', amount: '', paymentMethod: 'bank_transfer', paidAt: new Date().toISOString().slice(0, 10), invoiceId: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not record payment')); }
  };

  // ---- Credit Notes ----
  const updateCreditLine = (idx, field, value) => {
    setCreditNoteForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };
  const addCreditLine = () => setCreditNoteForm((prev) => ({ ...prev, lines: [...prev.lines, { description: '', quantity: '1', unitPrice: '0.00', vatRateId: '' }] }));

  const handleCreateCreditNote = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...creditNoteForm };
      if (!payload.invoiceId) delete payload.invoiceId;
      await apiClient.post('/invoicing/credit-notes', payload);
      success('✅ Credit note created');
      setShowForm(false);
      setCreditNoteForm({ customerId: '', invoiceId: '', creditNoteDate: new Date().toISOString().slice(0, 10), lines: [{ description: '', quantity: '1', unitPrice: '0.00', vatRateId: '' }] });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create credit note')); }
  };

  const customerInvoices = (customerId) => invoices.filter((i) => i.customerId === customerId && i.status !== 'void' && i.status !== 'paid');

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <style>{printStyles}</style>

      <div className="page-header no-print">
        <h1>Invoicing</h1>
        <p>Customers, products, quotes, invoices, payments, and credit notes</p>
      </div>

      <div className="no-print" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedInvoice(null); setSelectedQuote(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Customers' && (
        <section className="page-section no-print">
          <div className="section-header">
            <h2>Customers</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Customer'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddCustomer} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Name *</label><input required value={customerForm.name} onChange={(e) => setCustomerForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-field">
                <label>Type</label>
                <select value={customerForm.customerType} onChange={(e) => setCustomerForm(p => ({ ...p, customerType: e.target.value }))}>
                  <option value="business">Business</option>
                  <option value="individual">Individual</option>
                </select>
              </div>
              <div className="form-field">
                <label>Currency</label>
                <select value={customerForm.currencyCode} onChange={(e) => setCustomerForm(p => ({ ...p, currencyCode: e.target.value }))}>
                  <option value="">Organisation default</option>
                  {currencies.map((c) => <option key={c.id} value={c.code}>{c.code} ({c.symbol}) — {c.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Email</label><input type="email" value={customerForm.email} onChange={(e) => setCustomerForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div className="form-field"><label>Phone</label><input value={customerForm.phone} onChange={(e) => setCustomerForm(p => ({ ...p, phone: e.target.value }))} /></div>
              <div className="form-field"><label>Payment Terms (days)</label><input type="number" value={customerForm.paymentTermsDays} onChange={(e) => setCustomerForm(p => ({ ...p, paymentTermsDays: Number(e.target.value) }))} /></div>
              <div className="form-field"><label>VAT Number</label><input value={customerForm.vatNumber} onChange={(e) => setCustomerForm(p => ({ ...p, vatNumber: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Currency</th><th>Terms</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No customers yet</td></tr>
                ) : customers.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td>{c.email || '—'}</td>
                    <td>{symbolForCurrencyId(c.currencyId)} {currencies.find(cur => cur.id === c.currencyId)?.code || ''}</td>
                    <td>{c.paymentTermsDays} days</td>
                    <td><span className="badge" style={{ background: c.status === 'active' ? '#10b981' : '#9ca3af', color: 'white' }}>{c.status}</span></td>
                    <td>
                      <button className="action-icon delete" title="Delete" onClick={() => handleDeleteCustomer(c.id)}>
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Products' && (
        <section className="page-section no-print">
          <div className="section-header">
            <h2>Products & Services</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Item'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddProduct} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Name *</label><input required value={productForm.name} onChange={(e) => setProductForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-field"><label>SKU</label><input value={productForm.sku} onChange={(e) => setProductForm(p => ({ ...p, sku: e.target.value }))} /></div>
              <div className="form-field">
                <label>Type</label>
                <select value={productForm.itemType} onChange={(e) => setProductForm(p => ({ ...p, itemType: e.target.value }))}>
                  <option value="service">Service</option>
                  <option value="product">Product</option>
                </select>
              </div>
              <div className="form-field"><label>Default Unit Price</label><input type="number" step="0.01" value={productForm.defaultUnitPrice} onChange={(e) => setProductForm(p => ({ ...p, defaultUnitPrice: e.target.value }))} /></div>
              <div className="form-field full-width"><label>Description</label><input value={productForm.description} onChange={(e) => setProductForm(p => ({ ...p, description: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>SKU</th><th>Type</th><th>Unit Price</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No products yet</td></tr>
                ) : products.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{p.sku || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.itemType}</td>
                    <td>{p.defaultUnitPrice ? Number(p.defaultUnitPrice).toFixed(2) : '—'}</td>
                    <td><span className="badge" style={{ background: p.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{p.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="actions">
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleToggleActive(p.id, p.isActive)}>
                          {p.isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                        <button className="action-icon delete" title="Delete" onClick={() => handleDeleteProduct(p.id)}>
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Quotes' && (
        <>
          <section className="page-section no-print">
            <div className="section-header">
              <h2>Quotes</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Quote'}
              </button>
            </div>
            {!quoteWorkflowId && (
              <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
                No active approval workflow is set up for quotes yet — they can only be converted directly. Set one up under Workflow → Workflows (Applies To: Quote) to enable routed approval.
              </p>
            )}
            {showForm && (
              <form onSubmit={handleCreateQuote} style={{ marginBottom: '1.5rem' }}>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Customer *</label>
                    <select required value={quoteForm.customerId} onChange={(e) => setQuoteForm(p => ({ ...p, customerId: e.target.value }))}>
                      <option value="">-- select --</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({symbolForCurrencyId(c.currencyId)})</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label>Quote Date *</label><input type="date" required value={quoteForm.quoteDate} onChange={(e) => setQuoteForm(p => ({ ...p, quoteDate: e.target.value }))} /></div>
                  <div className="form-field"><label>Expiry Date</label><input type="date" value={quoteForm.expiryDate} onChange={(e) => setQuoteForm(p => ({ ...p, expiryDate: e.target.value }))} /></div>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                    Line Items {quoteForm.customerId && `(${quoteFormSymbol})`}
                  </label>
                  {quoteForm.lines.map((line, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <select value={line.productServiceId} onChange={(e) => updateQuoteLine(idx, 'productServiceId', e.target.value)} style={{ width: '160px', padding: '0.5rem' }}>
                        <option value="">Custom item</option>
                        {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input required placeholder="Description" value={line.description} onChange={(e) => updateQuoteLine(idx, 'description', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                      <input required type="number" step="0.01" placeholder="Qty" value={line.quantity} onChange={(e) => updateQuoteLine(idx, 'quantity', e.target.value)} style={{ width: '70px', padding: '0.5rem' }} />
                      <input required type="number" step="0.01" placeholder="Price" value={line.unitPrice} onChange={(e) => updateQuoteLine(idx, 'unitPrice', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                      <select value={line.vatRateId} onChange={(e) => updateQuoteLine(idx, 'vatRateId', e.target.value)} style={{ width: '120px', padding: '0.5rem' }}>
                        <option value="">No VAT</option>
                        {vatRates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.ratePercentage}%)</option>)}
                      </select>
                      <span style={{ width: '80px', fontWeight: 600 }}>{quoteFormSymbol}{quoteLineTotal(line)}</span>
                      {quoteForm.lines.length > 1 && (
                        <button type="button" className="action-icon delete" onClick={() => removeQuoteLine(idx)}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addQuoteLine}>
                    <FiPlus size={14} /> Add Line
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: '1rem', fontWeight: 700, fontSize: '1.1rem' }}>
                  Subtotal: {quoteFormSymbol}{quoteFormTotal}
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Quote</button>
                </div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Quote #</th><th>Customer</th><th>Date</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                  ) : quotes.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No quotes yet</td></tr>
                  ) : quotes.map((q) => {
                    const approval = approvalForQuote(q.id);
                    return (
                      <tr key={q.id}>
                        <td style={{ fontWeight: 600 }}>{q.quoteNumber || q.publicId}</td>
                        <td>{customerName(q.customerId)}</td>
                        <td>{new Date(q.quoteDate).toLocaleDateString()}</td>
                        <td>{symbolForCurrencyId(q.currencyId)}{Number(q.total).toFixed(2)}</td>
                        <td>
                          <span className="badge" style={{ background: q.status === 'converted' ? '#10b981' : '#f59e0b', color: 'white' }}>{q.status}</span>
                        </td>
                        <td>
                          <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewQuote(q)}>
                              <FiEye size={14} /> View
                            </button>
                            {approval && (
                              <span className="badge" style={{
                                background: approval.status === 'approved' ? '#10b981' : approval.status === 'rejected' ? '#ef4444' : approval.status === 'escalated' ? '#f59e0b' : '#3b82f6',
                                color: 'white',
                              }}>
                                Approval: {approval.status}
                              </span>
                            )}
                            {q.status === 'draft' && (
                              <>
                                {!approval && quoteWorkflowId && (
                                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSubmitQuoteForApproval(q)}>
                                    Submit for Approval
                                  </button>
                                )}
                                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleConvertQuote(q.id)}>
                                  <FiCheckCircle size={14} /> Convert to Invoice
                                </button>
                                <button className="action-icon delete" title="Delete" onClick={() => handleDeleteDraftQuote(q.id)}>
                                  <FiTrash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {selectedQuote && (
            <section className="page-section">
              <div className="section-header">
                <h2>Quote {selectedQuote.quoteNumber || selectedQuote.publicId} — {customerName(selectedQuote.customerId)}</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedQuote(null)}>Close</button>
              </div>
              <div className="stats-row">
                <div className="stat-box"><div className="label">Subtotal</div><div className="value">{symbolForCurrencyId(selectedQuote.currencyId)}{Number(selectedQuote.subtotal).toFixed(2)}</div></div>
                <div className="stat-box"><div className="label">VAT</div><div className="value">{symbolForCurrencyId(selectedQuote.currencyId)}{Number(selectedQuote.vatTotal).toFixed(2)}</div></div>
                <div className="stat-box"><div className="label">Total</div><div className="value">{symbolForCurrencyId(selectedQuote.currencyId)}{Number(selectedQuote.total).toFixed(2)}</div></div>
              </div>
              {quoteLines === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Line Total</th></tr></thead>
                    <tbody>
                      {quoteLines.map((l) => (
                        <tr key={l.id}>
                          <td>{l.description}</td>
                          <td>{l.quantity}</td>
                          <td>{symbolForCurrencyId(selectedQuote.currencyId)}{Number(l.unitPrice).toFixed(2)}</td>
                          <td style={{ fontWeight: 600 }}>{symbolForCurrencyId(selectedQuote.currencyId)}{Number(l.lineTotal).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'Invoices' && (
        <>
          <section className="page-section no-print">
            <div className="section-header">
              <h2>Invoices</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Invoice'}
              </button>
            </div>
            {!invoiceWorkflowId && (
              <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
                No active approval workflow is set up for invoices yet — drafts can only be issued directly. Set one up under Workflow → Workflows (Applies To: Invoice) to enable routed approval.
              </p>
            )}
            {showForm && (
              <form onSubmit={handleCreateInvoice} style={{ marginBottom: '1.5rem' }}>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Customer *</label>
                    <select required value={invoiceForm.customerId} onChange={(e) => setInvoiceForm(p => ({ ...p, customerId: e.target.value }))}>
                      <option value="">-- select --</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({symbolForCurrencyId(c.currencyId)})</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label>Invoice Date *</label><input type="date" required value={invoiceForm.invoiceDate} onChange={(e) => setInvoiceForm(p => ({ ...p, invoiceDate: e.target.value }))} /></div>
                  <div className="form-field"><label>Due Date</label><input type="date" value={invoiceForm.dueDate} onChange={(e) => setInvoiceForm(p => ({ ...p, dueDate: e.target.value }))} /></div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                    Line Items {invoiceForm.customerId && `(${invoiceFormSymbol})`}
                  </label>
                  {invoiceForm.lines.map((line, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <select value={line.productServiceId} onChange={(e) => updateInvoiceLine(idx, 'productServiceId', e.target.value)} style={{ width: '160px', padding: '0.5rem' }}>
                        <option value="">Custom item</option>
                        {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input required placeholder="Description" value={line.description} onChange={(e) => updateInvoiceLine(idx, 'description', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                      <input required type="number" step="0.01" placeholder="Qty" value={line.quantity} onChange={(e) => updateInvoiceLine(idx, 'quantity', e.target.value)} style={{ width: '70px', padding: '0.5rem' }} />
                      <input required type="number" step="0.01" placeholder="Price" value={line.unitPrice} onChange={(e) => updateInvoiceLine(idx, 'unitPrice', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                      <select value={line.vatRateId} onChange={(e) => updateInvoiceLine(idx, 'vatRateId', e.target.value)} style={{ width: '120px', padding: '0.5rem' }}>
                        <option value="">No VAT</option>
                        {vatRates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.ratePercentage}%)</option>)}
                      </select>
                      <span style={{ width: '80px', fontWeight: 600 }}>{invoiceFormSymbol}{invoiceLineTotal(line)}</span>
                      {invoiceForm.lines.length > 1 && (
                        <button type="button" className="action-icon delete" onClick={() => removeInvoiceLine(idx)}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addInvoiceLine}>
                    <FiPlus size={14} /> Add Line
                  </button>
                </div>

                <div style={{ textAlign: 'right', marginTop: '1rem', fontWeight: 700, fontSize: '1.1rem' }}>
                  Subtotal: {invoiceFormSymbol}{invoiceFormTotal}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Draft Invoice</button>
                </div>
              </form>
            )}

            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Invoice #</th><th>Customer</th><th>Date</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                  ) : invoices.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No invoices yet</td></tr>
                  ) : invoices.map((inv) => {
                    const approval = approvalForInvoice(inv.id);
                    return (
                      <tr key={inv.id}>
                        <td style={{ fontWeight: 600 }}>{inv.invoiceNumber || inv.publicId}</td>
                        <td>{customerName(inv.customerId)}</td>
                        <td>{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                        <td>{symbolForCurrencyId(inv.currencyId)}{Number(inv.total).toFixed(2)}</td>
                        <td>
                          <span className="badge" style={{
                            background: inv.status === 'paid' ? '#10b981' : inv.status === 'void' ? '#ef4444' : inv.status === 'issued' ? '#3b82f6' : '#f59e0b',
                            color: 'white',
                          }}>{inv.status}</span>
                        </td>
                        <td>
                          <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewInvoice(inv)}>
                              <FiEye size={14} /> View
                            </button>
                            {approval && (
                              <span className="badge" style={{
                                background: approval.status === 'approved' ? '#10b981' : approval.status === 'rejected' ? '#ef4444' : approval.status === 'escalated' ? '#f59e0b' : '#3b82f6',
                                color: 'white',
                              }}>
                                Approval: {approval.status}
                              </span>
                            )}
                            {inv.status === 'draft' && !approval && invoiceWorkflowId && (
                              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSubmitInvoiceForApproval(inv)}>
                                <FiSend size={14} /> Submit for Approval
                              </button>
                            )}
                            {inv.status === 'draft' && (
                              <>
                                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleIssueInvoice(inv.id)}>
                                  <FiSend size={14} /> Issue
                                </button>
                                <button className="action-icon delete" title="Delete draft" onClick={() => handleDeleteDraftInvoice(inv.id)}>
                                  <FiTrash2 size={16} />
                                </button>
                              </>
                            )}
                            {inv.status !== 'void' && inv.status !== 'paid' && inv.status !== 'draft' && (
                              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleVoidInvoice(inv.id)}>
                                <FiXCircle size={14} /> Void
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {selectedInvoice && (
            <section className="page-section invoice-print-area">
              <div className="section-header no-print">
                <h2>Invoice {selectedInvoice.invoiceNumber || selectedInvoice.publicId}</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handlePrint} title="Print">
                    <FiPrinter size={14} /> Print
                  </button>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handleShareEmail} title="Email">
                    <FiMail size={14} /> Email
                  </button>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handleShareWhatsApp} title="Share via WhatsApp">
                    <FiShare2 size={14} /> WhatsApp
                  </button>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedInvoice(null)}>Close</button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid #1f2937' }}>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>{companyName}</div>
                  {organisation?.companyRegistrationNumber && (
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Company No. {organisation.companyRegistrationNumber}</div>
                  )}
                  {organisation?.vatRegistered && organisation?.vatNumber && (
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>VAT No. {organisation.vatNumber}</div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>INVOICE</div>
                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>{selectedInvoice.invoiceNumber || selectedInvoice.publicId}</div>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  Billed to: <strong style={{ color: '#1f2937' }}>{customerName(selectedInvoice.customerId)}</strong>
                </p>
                <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                  Issued {new Date(selectedInvoice.invoiceDate).toLocaleDateString()}
                  {selectedInvoice.dueDate ? ` · Due ${new Date(selectedInvoice.dueDate).toLocaleDateString()}` : ''}
                </p>
              </div>

              {(() => {
                const sym = symbolForCurrencyId(selectedInvoice.currencyId);
                return (
                  <div className="stats-row">
                    <div className="stat-box"><div className="label">Subtotal</div><div className="value">{sym}{Number(selectedInvoice.subtotal).toFixed(2)}</div></div>
                    <div className="stat-box"><div className="label">VAT</div><div className="value">{sym}{Number(selectedInvoice.vatTotal).toFixed(2)}</div></div>
                    <div className="stat-box"><div className="label">Total</div><div className="value">{sym}{Number(selectedInvoice.total).toFixed(2)}</div></div>
                    <div className="stat-box"><div className="label">Amount Paid</div><div className="value" style={{ color: '#10b981' }}>{sym}{Number(selectedInvoice.amountPaid || 0).toFixed(2)}</div></div>
                    <div className="stat-box"><div className="label">Balance Due</div><div className="value" style={{ color: '#ef4444' }}>{sym}{(Number(selectedInvoice.total) - Number(selectedInvoice.amountPaid || 0)).toFixed(2)}</div></div>
                  </div>
                );
              })()}

              {invoiceLines === null ? (
                <p style={{ color: '#6b7280' }}>Loading breakdown...</p>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>VAT</th><th>Line Total</th></tr></thead>
                    <tbody>
                      {invoiceLines.map((l) => (
                        <tr key={l.id}>
                          <td>{l.description}</td>
                          <td>{l.quantity}</td>
                          <td>{symbolForCurrencyId(selectedInvoice.currencyId)}{Number(l.unitPrice).toFixed(2)}</td>
                          <td>{symbolForCurrencyId(selectedInvoice.currencyId)}{Number(l.vatAmount || 0).toFixed(2)}</td>
                          <td style={{ fontWeight: 600 }}>{symbolForCurrencyId(selectedInvoice.currencyId)}{Number(l.lineTotal).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {invoicePayments && invoicePayments.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Payments Received</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>Date</th><th>Method</th><th>Amount</th></tr></thead>
                      <tbody>
                        {invoicePayments.map((p) => (
                          <tr key={p.id}>
                            <td>{new Date(p.paidAt).toLocaleDateString()}</td>
                            <td style={{ textTransform: 'capitalize' }}>{p.paymentMethod?.replace('_', ' ')}</td>
                            <td style={{ fontWeight: 600 }}>{symbolForCurrencyId(p.currencyId)}{Number(p.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {invoiceCreditNotes && invoiceCreditNotes.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Credit Notes Applied</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>Credit Note #</th><th>Date</th><th>Amount</th></tr></thead>
                      <tbody>
                        {invoiceCreditNotes.map((cn) => (
                          <tr key={cn.id}>
                            <td>{cn.creditNoteNumber || cn.publicId}</td>
                            <td>{new Date(cn.creditNoteDate).toLocaleDateString()}</td>
                            <td style={{ fontWeight: 600 }}>{symbolForCurrencyId(cn.currencyId)}{Number(cn.total).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'Payments' && (
        <section className="page-section no-print">
          <div className="section-header">
            <h2>Record Payment</h2>
          </div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Payments aren't deletable here — they're real financial records tied to what's been marked as paid on an invoice. If one was recorded in error, the correct fix is a credit note or a manual adjustment, not deletion.
          </p>
          <form onSubmit={handleRecordPayment} className="form-grid">
            <div className="form-field">
              <label>Customer *</label>
              <select required value={paymentForm.customerId} onChange={(e) => setPaymentForm(p => ({ ...p, customerId: e.target.value, invoiceId: '' }))}>
                <option value="">-- select --</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({symbolForCurrencyId(c.currencyId)})</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Invoice to Pay *</label>
              <select required value={paymentForm.invoiceId} onChange={(e) => setPaymentForm(p => ({ ...p, invoiceId: e.target.value }))} disabled={!paymentForm.customerId}>
                <option value="">-- select --</option>
                {customerInvoices(paymentForm.customerId).map((i) => <option key={i.id} value={i.id}>{i.invoiceNumber || i.publicId} — {symbolForCurrencyId(i.currencyId)}{Number(i.total).toFixed(2)}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Amount {paymentForm.customerId ? `(${symbolForCustomer(paymentForm.customerId)})` : ''} *</label><input required type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))} /></div>
            <div className="form-field">
              <label>Method</label>
              <select value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="direct_debit">Direct Debit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-field"><label>Date *</label><input required type="date" value={paymentForm.paidAt} onChange={(e) => setPaymentForm(p => ({ ...p, paidAt: e.target.value }))} /></div>
            <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Record Payment</button></div>
          </form>

          <div className="table-container" style={{ marginTop: '1.5rem' }}>
            <table className="data-table">
              <thead><tr><th>Customer</th><th>Amount</th><th>Method</th><th>Date</th></tr></thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No payments recorded yet</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{customerName(p.customerId)}</td>
                    <td>{symbolForCurrencyId(p.currencyId)}{Number(p.amount).toFixed(2)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.paymentMethod?.replace('_', ' ')}</td>
                    <td>{new Date(p.paidAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Credit Notes' && (
        <section className="page-section no-print">
          <div className="section-header">
            <h2>Credit Notes</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Credit Note'}
            </button>
          </div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Credit notes aren't deletable either, for the same reason as payments — they're a formal correction to a real financial record.
          </p>
          {showForm && (
            <form onSubmit={handleCreateCreditNote} style={{ marginBottom: '1.5rem' }}>
              <div className="form-grid">
                <div className="form-field">
                  <label>Customer *</label>
                  <select required value={creditNoteForm.customerId} onChange={(e) => setCreditNoteForm(p => ({ ...p, customerId: e.target.value, invoiceId: '' }))}>
                    <option value="">-- select --</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({symbolForCurrencyId(c.currencyId)})</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Against Invoice (optional)</label>
                  <select value={creditNoteForm.invoiceId} onChange={(e) => setCreditNoteForm(p => ({ ...p, invoiceId: e.target.value }))} disabled={!creditNoteForm.customerId}>
                    <option value="">-- none --</option>
                    {invoices.filter((i) => i.customerId === creditNoteForm.customerId).map((i) => <option key={i.id} value={i.id}>{i.invoiceNumber || i.publicId}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Date *</label><input required type="date" value={creditNoteForm.creditNoteDate} onChange={(e) => setCreditNoteForm(p => ({ ...p, creditNoteDate: e.target.value }))} /></div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                  Line Items {creditNoteForm.customerId && `(${symbolForCustomer(creditNoteForm.customerId)})`}
                </label>
                {creditNoteForm.lines.map((line, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input required placeholder="Description" value={line.description} onChange={(e) => updateCreditLine(idx, 'description', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                    <input required type="number" step="0.01" placeholder="Qty" value={line.quantity} onChange={(e) => updateCreditLine(idx, 'quantity', e.target.value)} style={{ width: '70px', padding: '0.5rem' }} />
                    <input required type="number" step="0.01" placeholder="Price" value={line.unitPrice} onChange={(e) => updateCreditLine(idx, 'unitPrice', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                    <select value={line.vatRateId} onChange={(e) => updateCreditLine(idx, 'vatRateId', e.target.value)} style={{ width: '120px', padding: '0.5rem' }}>
                      <option value="">No VAT</option>
                      {vatRates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.ratePercentage}%)</option>)}
                    </select>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addCreditLine}>
                  <FiPlus size={14} /> Add Line
                </button>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Credit Note</button>
              </div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Customer</th><th>Date</th><th>Total</th></tr></thead>
              <tbody>
                {creditNotes.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No credit notes yet</td></tr>
                ) : creditNotes.map((cn) => (
                  <tr key={cn.id}>
                    <td style={{ fontWeight: 600 }}>{customerName(cn.customerId)}</td>
                    <td>{new Date(cn.creditNoteDate).toLocaleDateString()}</td>
                    <td>{symbolForCurrencyId(cn.currencyId)}{Number(cn.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default Invoicing;